import {defineStore} from 'pinia';
import {StrategyPayload} from '@/brief-hub/store/modules/brief/types';
import StrategyBriefResource from '@/brief-hub/brief/StrategyBriefResource';
import {
    Address,
    BriefApiExperienceLevel,
    BriefStartDateFlexibility,
    BriefStartDateType,
    BriefSubmissionRequest,
    BriefWorkingStatus,
    FreelancerAvailability,
    LanguageResource,
    ProjectDuration,
    RejectReason,
    StrategyFreelancerBriefRequest,
    StrategyFreelancerFunctionalScopesBriefRequest,
    StrategyFreelancerIndustryExpertisesBriefRequest,
    StrategyFreelancerSkillsBriefRequest,
    StrategyMissionDescriptionBriefRequest,
    StrategyWhenConditionsBriefRequest,
    StrategyWhereConditionsBriefRequest,
} from '@/common/api/api-raw-types';
import {computed, ComputedRef, ref, Ref} from 'vue';
import {isPayloadDifferent} from '@/brief-hub/store/modules/brief/actions-utils';
import strategyBriefService from '@/brief-hub/brief/StrategyBriefService';
import {HttpServiceResponse} from 'vue3-starter/src/typescript/http/http';
import {v4 as uuidv4} from 'uuid';

export interface BriefStrategyFreelancerProfile {
    selectedJobTitle: string | null;
    selectedCategory: string | null;
    selectedCategoryLabel: string | null;
    selectedFamily: string | null;
    selectedFamilyLabel: string | null;
    selectedExperienceLevel: BriefApiExperienceLevel | null;
    selectedDailyFreelancerRate: number | null;
}

export interface BriefStrategyFunctionalScopes {
    globalScopeHumanId: string | null;
    specificScopeHumanId: string | null;
}

export interface BriefStrategyIndustryExpertises {
    businessSectorHumanId: string | null;
    businessIndustryHumanId: string | null;
}

export interface BriefStrategyFreelancerSkills {
    jobTitle: string;
    category: string | null;
    freelancerSkills: string[] | null;
    languages: LanguageResource[] | null;
}

export interface BriefStrategyWhereConditions {
    address?: Address | null;
    remoteFrequency?: number | null;
    workingStatus: BriefWorkingStatus | null;
    freelancerAvailability: FreelancerAvailability | null;
}

export interface BriefStrategyMissionDescription {
    description: string | null;
    title: string | null;
}

export interface BriefStrategyConditionsWhen {
    selectedStartDate: Date | null;
    selectedStartDateType: BriefStartDateType | null;
    selectedStartDateFlexibility: BriefStartDateFlexibility | null;
    selectedAvailability: FreelancerAvailability | null;
    selectedDuration: ProjectDuration | null;
    selectedBudget: number | null;
    briefCurrencyCode: string | null;
    selectedDailyFreelancerRate: number | null;
}

export const useBriefStrategyStore = defineStore('briefStrategy', () => {
    // State
    const loading: Ref<boolean> = ref(false);
    const error: Ref<boolean> = ref(false);
    const saving: Ref<boolean> = ref(false);
    const brief = ref<StrategyBriefResource | null>(null);
    const payload: Ref<StrategyPayload | null> = ref(null);
    const originReferrer = ref<string | null>(null);
    const redirectionReason = ref<RejectReason | null>(null);
    const briefFunnelId = ref<string | null>(uuidv4());

    // Getters
    const briefStrategyFreelancerProfile: ComputedRef = computed((): BriefStrategyFreelancerProfile => {
        const selectedJobTitle: string | null = brief.value?.profileDetails?.freelancerJobTitle || null;
        const selectedCategory: string | null = brief.value?.profileDetails.freelancerCategories[0] || null;
        const selectedCategoryLabel: string | null = brief.value?.profileDetails.freelancerCategoryLabels[0] || null;
        const selectedFamily: string | null = brief.value?.profileDetails.freelancerRequiredCategories[0].familyId || null;
        const selectedFamilyLabel: string | null = brief.value?.profileDetails.freelancerRequiredCategories[0].label || null;
        const selectedExperienceLevel: BriefApiExperienceLevel | null = brief.value?.profileDetails?.experienceLevel || null;
        const selectedDailyFreelancerRate: number | null = brief.value?.briefDetails?.dailyFreelancerRate || null;
        return {
            selectedJobTitle,
            selectedCategory,
            selectedFamily,
            selectedCategoryLabel,
            selectedExperienceLevel,
            selectedFamilyLabel,
            selectedDailyFreelancerRate,
        };
    });

    const briefStrategyFunctionalScopes: ComputedRef = computed((): BriefStrategyFunctionalScopes => {
        const globalScopeHumanId = brief.value?.profileDetails.functionalScopes[0]?.globalScopeHumanId || null;
        const specificScopeHumanId = brief.value?.profileDetails.functionalScopes[0]?.specificScopeHumanId || null;
        return {
            globalScopeHumanId,
            specificScopeHumanId,
        };
    });

    const briefStrategyIndustryExpertises: ComputedRef = computed((): BriefStrategyIndustryExpertises => {
        const businessSectorHumanId = brief.value?.profileDetails.industryExpertises[0]?.businessSectorHumanId || null;
        const businessIndustryHumanId = brief.value?.profileDetails.industryExpertises[0]?.businessIndustryHumanId || null;
        return {
            businessSectorHumanId,
            businessIndustryHumanId,
        };
    });

    const briefStrategyWhereConditions: ComputedRef = computed((): BriefStrategyWhereConditions => {
        const address = brief.value?.briefDetails.address || null;
        const remoteFrequency = brief.value?.briefDetails.remoteFrequency || null;
        const workingStatus = brief.value?.briefDetails.workingStatus || null;
        const freelancerAvailability = brief.value?.briefDetails.freelancerAvailability || null;
        return {
            address,
            remoteFrequency,
            workingStatus,
            freelancerAvailability,
        };
    });

    const briefStrategyFreelancerSkills: ComputedRef = computed((): BriefStrategyFreelancerSkills => {
        const jobTitle: string = brief.value?.profileDetails?.freelancerJobTitle || '';
        const category: string | null = brief.value?.profileDetails.freelancerCategories[0] || null;
        const freelancerSkills: string[] | null = brief.value?.profileDetails?.freelancerSkills || null;
        const languages: LanguageResource[] | null = brief.value?.profileDetails.languages || null;
        return {
            jobTitle,
            category,
            freelancerSkills,
            languages,
        };
    });

    const briefStrategyMission: ComputedRef = computed((): BriefStrategyMissionDescription => {
        const description: string | null = brief?.value?.briefDetails.description || null;
        const title: string | null = brief?.value?.briefDetails.title || null;
        return {description, title};
    });

    const briefStrategyConditionsWhen: ComputedRef = computed((): BriefStrategyConditionsWhen => {
        const startDate: string | null = brief?.value?.briefDetails?.startDate || null;
        const selectedStartDate: Date | null = startDate ? new Date(startDate) : null;
        const selectedStartDateType: BriefStartDateType | null = brief?.value?.briefDetails?.startDateType || null;
        const selectedStartDateFlexibility: BriefStartDateFlexibility | null = brief?.value?.briefDetails?.startDateFlexibility || null;
        const selectedAvailability: FreelancerAvailability | null = brief.value?.briefDetails?.freelancerAvailability || null;
        const selectedDuration: ProjectDuration | null = brief.value?.briefDetails?.projectDuration ?? null;
        const selectedBudget: number | null = brief.value?.briefDetails?.budget || null;
        const briefCurrencyCode: string | null = brief.value?.briefDetails?.currencyCode || null;
        const selectedDailyFreelancerRate: number | null = brief.value?.briefDetails?.dailyFreelancerRate || null;
        return {
            selectedStartDate,
            selectedAvailability,
            selectedDuration,
            selectedBudget,
            briefCurrencyCode,
            selectedDailyFreelancerRate,
            selectedStartDateType,
            selectedStartDateFlexibility,
        };
    });

    // Actions
    function getStrategyBrief(briefId: string) {
        loading.value = true;
        error.value = false;

        return strategyBriefService.retrieveBrief(briefId).then((response) => {
            loading.value = false;
            const [data, err] = response;
            if (!data || err) {
                error.value = true;
                return [null, err];
            }
            return updateBrief(data);
        });
    }

    async function saveStrategyBrief(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        switch (payload.value?.type) {
            case 'StrategyFreelancerBriefRequest':
                return await saveStrategyBriefFreelancerProfile();
            case 'StrategyFreelancerFunctionalScopesBriefRequest':
                return await saveStrategyBriefFunctionalScopes();
            case 'StrategyFreelancerIndustryExpertisesBriefRequest':
                return await saveStrategyBriefIndustryExpertises();
            case 'StrategyFreelancerSkillsBriefRequest':
                return await saveStrategyBriefFreelancerSkills();
            case 'StrategyWhereConditionsBriefRequest':
                return await saveStrategyWhereConditionsBriefRequest();
            case 'StrategyMissionDescriptionBriefRequest':
                return await saveStrategyBriefMissionDescription();
            case 'StrategyWhenConditionsBriefRequest':
                return await saveStrategyBriefWhenConditions();
            case 'BriefSubmissionRequest':
                return await saveStrategyBriefSubmission();
            default:
                return Promise.resolve([null, new Error('Unknown payload type')]);
        }
    }

    async function saveStrategyBriefFreelancerProfile(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        if (!payload.value) {
            return [null, new Error('payload is null')];
        }
        // brief does not exist => so we create it
        if (!brief.value) {
            const [data, error] = await saveAndUpdateBrief(() =>
                strategyBriefService.createStrategyBrief(payload.value!.payload as StrategyFreelancerBriefRequest),
            );
            return [data, error];
        }
        // brief exists => so we update it
        const requestPayload = payload.value.payload as StrategyFreelancerBriefRequest;

        if (isPayloadDifferent(requestPayload, brief.value.profileDetails)) {
            return await saveAndUpdateBrief(() => brief.value!.strategySaveFreelancerProfile(requestPayload));
        }
        return [brief.value, null];
    }

    async function saveStrategyBriefFunctionalScopes(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        if (!payload.value) {
            return [null, new Error('payload is null')];
        }

        const requestPayload = payload.value.payload as StrategyFreelancerFunctionalScopesBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.value!.briefDetails)) {
            return saveAndUpdateBrief(() => brief.value!.strategySaveFunctionalScopes(requestPayload));
        }
        return [brief.value, null];
    }

    async function saveStrategyBriefIndustryExpertises(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        if (!payload.value) {
            return [null, new Error('payload is null')];
        }

        const requestPayload = payload.value.payload as StrategyFreelancerIndustryExpertisesBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.value!.briefDetails)) {
            return saveAndUpdateBrief(() => brief.value!.strategySaveIndustryExpertises(requestPayload));
        }
        return [brief.value, null];
    }

    async function saveStrategyBriefWhenConditions(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        if (!payload.value) {
            return [null, new Error('payload is null')];
        }

        const requestPayload = payload.value.payload as StrategyWhenConditionsBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.value!.briefDetails)) {
            return saveAndUpdateBrief(() => brief.value!.strategySaveWhenConditions(requestPayload));
        }
        return [brief.value, null];
    }

    async function saveStrategyBriefSubmission(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        if (!payload.value) {
            return [null, new Error('payload is null')];
        }

        const requestPayload = payload.value.payload as BriefSubmissionRequest;
        if (isPayloadDifferent(requestPayload, brief.value!.briefDetails)) {
            return saveAndUpdateBrief(() => brief.value!.strategySubmitBrief(requestPayload));
        }
        return [brief.value, null];
    }

    async function saveStrategyBriefFreelancerSkills(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        if (!payload.value) {
            return [null, new Error('payload is null')];
        }

        const requestPayload = payload.value.payload as StrategyFreelancerSkillsBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.value!.briefDetails)) {
            return saveAndUpdateBrief(() => brief.value!.strategySaveFreelancerSkills(requestPayload));
        }
        return [brief.value, null];
    }

    async function saveStrategyWhereConditionsBriefRequest(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        if (!payload.value) {
            return [null, new Error('payload is null')];
        }

        const requestPayload = payload.value.payload as StrategyWhereConditionsBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.value!.briefDetails)) {
            return saveAndUpdateBrief(() => brief.value!.strategySaveWhereConditions(requestPayload));
        }
        return [brief.value, null];
    }

    async function saveStrategyBriefMissionDescription(): Promise<HttpServiceResponse<StrategyBriefResource>> {
        if (!payload.value) {
            return [null, new Error('payload is null')];
        }

        const requestPayload = payload.value.payload as StrategyMissionDescriptionBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.value!.briefDetails)) {
            return saveAndUpdateBrief(() => brief.value!.strategySaveMissionDescription(requestPayload));
        }
        return [brief.value, null];
    }

    function updateBrief(newBrief: StrategyBriefResource) {
        brief.value = newBrief;
        payload.value = null;
    }

    function updatePayload(newPayload: StrategyPayload) {
        payload.value = newPayload;
    }

    function updateOriginReferrer(newReferrer: string) {
        if (!originReferrer.value && newReferrer.includes(window.location.hostname) && newReferrer !== window.location.href) {
            originReferrer.value = newReferrer;
        }
    }

    function resetPayload() {
        payload.value = null;
    }

    function resetStore() {
        brief.value = null;
        payload.value = null;
        redirectionReason.value = null;
        briefFunnelId.value = uuidv4();
    }

    // Utils
    const saveAndUpdateBrief: (
        saveBrief: () => Promise<HttpServiceResponse<StrategyBriefResource>>,
    ) => Promise<HttpServiceResponse<StrategyBriefResource>> = async (saveBrief) => {
        const [newBrief, error] = await saveBrief();
        if (newBrief && !error) {
            saving.value = true;
            updateBrief(newBrief);
            return [newBrief, null];
        }
        return [null, error];
    };

    return {
        brief,
        briefFunnelId,
        payload,
        saving,
        redirectionReason,
        briefStrategyFreelancerProfile,
        briefStrategyFunctionalScopes,
        briefStrategyIndustryExpertises,
        briefStrategyFreelancerSkills,
        briefStrategyWhereConditions,
        briefStrategyMission,
        briefStrategyConditionsWhen,
        originReferrer,
        getStrategyBrief,
        saveStrategyBrief,
        updatePayload,
        resetPayload,
        resetStore,
        updateOriginReferrer,
    };
});
